import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService.js';
import gigService from '../../services/gigService.js';
import { useTranslation } from '../../i18n/useTranslation';

const DAY_MS = 86400000;

function toLocalMidnight(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function CompletionRing({ rate }) {
  const r = 18;
  const circ = 2 * Math.PI * r;
  const dash = (rate / 100) * circ;
  return (
    <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
      <svg width="48" height="48" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="24" cy="24" r={r} fill="none" stroke="var(--color-monotone-2)" strokeWidth="4" />
        <circle cx="24" cy="24" r={r} fill="none"
          stroke="var(--color-primary)" strokeWidth="4"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
      </svg>
      <span style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700,
      }}>{rate}%</span>
    </div>
  );
}

const STATUS_COLOR = {
  not_started: 'var(--color-monotone-3)',
  in_progress: 'var(--color-primary)',
  delayed:     'var(--color-warning)',
  completed:   'var(--color-success)',
};

function HomeWidgetGigTasks() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [summary, setSummary]   = useState(null);
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const isLoggedIn = authService.isLoggedIn();

  const load = useCallback(async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    setLoading(true);
    try {
      const [sumRes, taskRes] = await Promise.all([
        fetch('/api/user-stats/gig/summary', { credentials: 'include' }).then(r => r.json()),
        gigService.getMyTasks('active').catch(() => ({ success: false })),
      ]);
      if (sumRes.success)  setSummary(sumRes.summary);
      if (taskRes.success) setTasks(taskRes.tasks || []);
    } catch {}
    setLoading(false);
  }, [isLoggedIn]);

  useEffect(() => { load(); }, [load]);

  if (!isLoggedIn) return (
    <p style={{ color: 'var(--color-monotone-4)', fontSize: 13, textAlign: 'center', margin: 0 }}>
      {t('homePage.widgets.loginForGigTasks')}
    </p>
  );

  if (loading) return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div className="skeleton-pulse" style={{ width: 48, height: 48, borderRadius: 24 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div className="skeleton-pulse" style={{ height: 14, borderRadius: 7, width: '60%' }} />
        <div className="skeleton-pulse" style={{ height: 14, borderRadius: 7, width: '80%' }} />
      </div>
    </div>
  );

  const today = new Date(); today.setHours(0,0,0,0);

  const overdue  = tasks.filter(task => {
    if (task.plan_status === 'delayed') return true;
    const e = toLocalMidnight(task.plan_end_date);
    return e && e < today;
  });
  const upcoming = tasks.filter(task => !overdue.includes(task));

  const summaryRows = [
    { key: 'activeSpaces', value: summary?.activeSpaces ?? 0 },
    { key: 'assignedPlans', value: summary?.assignedPlans ?? 0 },
    { key: 'completed', value: summary?.completedPlans ?? 0 },
    { key: 'overdue', value: summary?.overduePlans ?? 0, warn: (summary?.overduePlans ?? 0) > 0 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: 'var(--color-monotone-1)', borderRadius: 10 }}>
        <CompletionRing rate={summary?.completionRate ?? 0} />
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px' }}>
          {summaryRows.map(({ key, value, warn }) => (
            <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: 'var(--color-monotone-4)' }}>{t(`homePage.widgets.${key}`)}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: warn ? 'var(--color-error)' : 'var(--color-text)' }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {tasks.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', color: 'var(--color-monotone-4)', fontSize: 12 }}>
          <span className="material-symbols-rounded" style={{ fontSize: 18 }}>task_alt</span>
          {t('homePage.widgets.noAssignedPlans')}
        </div>
      ) : (
        <div>
          {overdue.slice(0, 2).map(task => {
            const e = toLocalMidnight(task.plan_end_date);
            const days = e ? Math.round((e.getTime() - today.getTime()) / DAY_MS) : null;
            return (
              <div
                key={task.plan_id}
                onClick={() => navigate(`/gig/space/${task.space_id}/project/${task.project_id}/plan/${task.plan_id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--color-monotone-1)', cursor: 'pointer' }}
              >
                <span style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--color-error)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.plan_title}</span>
                <span style={{ fontSize: 10, color: 'var(--color-error)', fontWeight: 600, flexShrink: 0 }}>
                  {days !== null && days < 0
                    ? t('gig.task.daysOverdue', { count: Math.abs(days) })
                    : t('gig.task.today')}
                </span>
              </div>
            );
          })}
          {upcoming.slice(0, 3).map(task => {
            const e = toLocalMidnight(task.plan_end_date);
            const days = e ? Math.round((e.getTime() - today.getTime()) / DAY_MS) : null;
            return (
              <div
                key={task.plan_id}
                onClick={() => navigate(`/gig/space/${task.space_id}/project/${task.project_id}/plan/${task.plan_id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--color-monotone-1)', cursor: 'pointer' }}
              >
                <span style={{ width: 7, height: 7, borderRadius: 4, background: STATUS_COLOR[task.plan_status] || 'var(--color-monotone-3)', flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.plan_title}</span>
                {days !== null && (
                  <span style={{ fontSize: 10, color: days <= 3 ? 'var(--color-warning)' : 'var(--color-monotone-4)', flexShrink: 0 }}>
                    {days === 0 ? t('gig.task.today') : t('homePage.widgets.deadlineCountdown', { days })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn-rtg btn-monotone-2"
          onClick={() => navigate('/gig')}
          style={{ fontSize: 12 }}
        >
          {t('homePage.widgets.goToGig')}
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>arrow_forward</span>
        </button>
      </div>
    </div>
  );
}

export default HomeWidgetGigTasks;
